import type { ComponentParameter } from "../types";

/**
 * Compute a stable option-value string for a parameter within an element's
 * parameters array. For bucketed parameters the qualified name is the natural
 * key; for no-bucket slots (`parameterQualifiedName === null`) a display-name
 * prefix keeps the value unique and deterministic.
 */
export function getParamOptionValue(param: ComponentParameter): string {
  return param.parameterQualifiedName !== null
    ? param.parameterQualifiedName
    : `__display__${param.displayName}`;
}

/**
 * Find a parameter in an element's parameters list by its option-value string.
 * Returns `undefined` when no match is found.
 */
export function findParamByOptionValue(
  params: ComponentParameter[],
  optionValue: string,
): ComponentParameter | undefined {
  return params.find((p) => getParamOptionValue(p) === optionValue);
}

type ColumnSelection = ComponentParameter | null | undefined;

/**
 * Option values already mapped to a different CSV column in this scope.
 * One parameter may map to at most one column (company mapping key).
 *
 * When `visibleColumns` is set, selections for headers outside that list
 * (e.g. assignment date columns hidden from the mapping table) are ignored
 * so they do not block the parameter for remaining data columns.
 */
export function takenParamOptionValuesExcludingColumn(
  scopeSelections: Record<string, ColumnSelection>,
  exceptColumn: string,
  visibleColumns?: readonly string[],
): Set<string> {
  const taken = new Set<string>();
  const visible =
    visibleColumns === undefined ? null : new Set(visibleColumns);
  for (const [column, selection] of Object.entries(scopeSelections)) {
    if (column === exceptColumn || selection == null) continue;
    if (visible !== null && !visible.has(column)) continue;
    taken.add(getParamOptionValue(selection));
  }
  return taken;
}
